import * as React from 'react';
import styled, { css } from 'styled-components';
import { LoadingIndicator } from '../LoadingIndicator';
import { ComponentLStyling, ComponentMStyling, ComponentSStyling, ComponentTextStyle } from '../styles';
import { COLORS, focusStyles, invertedFocusStyles } from '../styles';
import { Size, Testable } from '../types';
import { defaultOnMouseDownHandler } from '../common';

const getBorderRadius = (flatEdge: string | undefined, radius: number) => {
  switch (flatEdge) {
    case 'left':
      return `0px ${radius}px ${radius}px 0px`;

    case 'right':
      return `${radius}px 0px 0px ${radius}px`;

    case 'none':
    default:
      return `${radius}px`;
  }
};

export const ButtonPrimaryStyled = styled.button<ButtonProps>`
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  position: relative;
  box-sizing: border-box;
  min-height: ${(props) => (props.size === Size.Large ? '56px' : '48px')};
  &.loading-state {
    cursor: wait;
  }
  div.button-content {
    ${(props) =>
      props.size === Size.Large
        ? ComponentLStyling(ComponentTextStyle.Bold, props?.colorTheme === 'dark' ? COLORS.primary_800 : COLORS.white)
        : props.size === Size.Small
        ? ComponentSStyling(ComponentTextStyle.Bold, props?.colorTheme === 'dark' ? COLORS.primary_800 : COLORS.white)
        : ComponentMStyling(ComponentTextStyle.Bold, props?.colorTheme === 'dark' ? COLORS.primary_800 : COLORS.white)};

    background-color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1 : props?.colorTheme === 'dark' ? COLORS.white : COLORS.primary)};
    border-radius: ${(props) => getBorderRadius(props.flatEdge, 8)};

    width: ${(props) => props.width};
    position: relative;

    min-height: ${(props) => (props.size === Size.Large ? '56px' : props.size === Size.Small ? '32px' : '40px')};
    min-width: ${(props) => props.minWidth};

    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${(props) => (props.size === Size.Large ? '14px 16px' : props.size === Size.Small ? '6px 8px' : '8px 12px')};

    span.icon:not(.loading) {
      pointer-events: none;
    }

    span.loading {
      opacity: 0;
    }

    .icon {
      margin: ${(props) => (props.size === Size.Medium ? props.iconOnly ? '-2px -8px' : '' : (props.size === Size.Small ? props.iconOnly ? '-2px -6px' : '' : '' ))};
      width: ${(props) => (props.size === Size.Large ? '28px' : props.size === Size.Small ? '20px' : '24px')};
      height: ${(props) => (props.size === Size.Large ? '28px' : props.size === Size.Small ? '20px' : '24px')};
      svg {
        width: ${(props) => (props.size === Size.Large ? '28px' : props.size === Size.Small ? '20px' : '24px')} !important;
        height: ${(props) => (props.size === Size.Large ? '28px' : props.size === Size.Small ? '20px' : '24px')} !important;
      }
    }

    .label {
      margin: 0 4px;
    }

    div {
      position: absolute;
      width: 100%;
      svg {
        height: ${(props) => (props.size === Size.Large ? '28px' : props.size === Size.Small ? '20px' : '24px')};
        width: ${(props) => (props.size === Size.Large ? '28px' : props.size === Size.Small ? '20px' : '24px')};
        path {
          fill: ${(props) => (props.colorTheme === 'dark' ? COLORS.primary : COLORS.white)};
        }
      }
    }
  }
  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_700 : props?.colorTheme === 'dark' ? COLORS.primary_100 : COLORS.primary_700)};
    color: ${(props) => (props?.colorTheme === 'dark' ? COLORS.primary_600 : COLORS.white)};
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_800 : props?.colorTheme === 'dark' ? COLORS.primary_200 : COLORS.primary_800)};
    color: ${(props) => (props?.colorTheme === 'dark' ? COLORS.primary_800 : COLORS.white)};
  }

  &:focus,
  &.focus-state {
    ${(props) => (props.invertFocus || (props.invertFocus === undefined && props.colorTheme === 'dark') ? invertedFocusStyles : focusStyles)}
  }
  &:disabled,
  &.disabled-state {
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    pointer-events: none;
    background-color: ${(props) => (props.colorTheme === 'dark' ? COLORS.primary_700 : COLORS.neutral_100)};
    color: ${(props) => (props.colorTheme === 'dark' ? COLORS.primary_800 : COLORS.neutral_300)};
  }
`;

export const ButtonSecondaryStyled = styled(ButtonPrimaryStyled)<ButtonProps>`
  div.button-content {
    color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1 : props?.colorTheme === 'dark' ? COLORS.white : COLORS.primary)};
    background-color: transparent;
    div svg path {
      fill: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    }
    box-shadow: inset 0 0 0 2px ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1 : props?.colorTheme === 'dark' ? COLORS.white : COLORS.primary)};
    
    padding: ${(props) => (props.size === Size.Large ? '12px 16px' : props.size === Size.Small ? '4px 8px' : '6px 12px')};
  }

  &:hover > div.button-content,
  &.hover-state > div.button-content {
    color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_700 : props?.colorTheme === 'dark' ? COLORS.primary_200 : COLORS.primary_700)};
    box-shadow: inset 0 0 0 2px ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_700 : props?.colorTheme === 'dark' ? COLORS.primary_200 : COLORS.primary_700)};
    
    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
        }
      }
    }
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_800 : props?.colorTheme === 'dark' ? COLORS.primary_300 : COLORS.primary_800)};
    box-shadow: inset 0 0 0 2px ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_800 : props?.colorTheme === 'dark' ? COLORS.primary_300 : COLORS.primary_800)};
    
    background-color: transparent;
    div {
      svg {
        path {
          fill: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_800 : props?.colorTheme === 'dark' ? COLORS.white : COLORS.primary_800)};
        }
      }
    }
  }

  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: transparent;
    color: ${(props) => (props?.colorTheme === 'dark' ? COLORS.primary_700 : COLORS.neutral_300)};
    box-shadow: inset 0 0 0 2px ${(props) => (props?.colorTheme === 'dark' ? COLORS.primary_700 : COLORS.neutral_100)};
  }
`;

export const ButtonTertiaryStyled = styled(ButtonPrimaryStyled)<ButtonProps>`
  div.button-content {
    color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1 : props.colorTheme === 'dark' ? COLORS.white : COLORS.primary_500)};
    background-color: transparent;

    div svg path {
      fill: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1 : props.colorTheme === 'dark' ? COLORS.white : COLORS.primary_500)};
    }

    padding: ${(props) => (props.size === Size.Large ? '12px 16px' : props.size === Size.Small ? '4px 8px' : '6px 12px')};
  }

  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_20 : props.colorTheme === 'dark' ? COLORS.primary_700 : COLORS.primary_20)};
    color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_700 : props.colorTheme === 'dark' ? COLORS.white : COLORS.primary_700)};

    div {
      svg {
        path {
          fill: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_700 : props.colorTheme === 'dark' ? COLORS.white : COLORS.primary_700)};
        }
      }
    }
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_100 : props.colorTheme === 'dark' ? COLORS.primary_600 : COLORS.primary_100)};
    color: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_800 : props.colorTheme === 'dark' ? COLORS.white : COLORS.primary_800)};

    div {
      svg {
        path {
          fill: ${(props) => (props.colorTheme === 'teal' ? COLORS.accent1_800 : props.colorTheme === 'dark' ? COLORS.white : COLORS.primary_800)};
        }
      }
    }
  }

  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: transparent !important;
    color: ${COLORS.primary_700};
    border-color: transparent !important;
  }
`;

export const ButtonPositiveStyled = styled(ButtonPrimaryStyled)<ButtonProps>`
  div.button-content {
    color: white;
    background-color: ${COLORS.correct_500};
  }
  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: ${COLORS.correct_700};
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: ${COLORS.correct_800};
  }

  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: ${COLORS.neutral_100};
    color: ${COLORS.neutral_300};
  }
`;

export const ButtonCriticalStyled = styled(ButtonPrimaryStyled)<ButtonProps>`
  div.button-content {
    color: white;
    background-color: ${COLORS.critical_500};
  }
  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: ${COLORS.critical_700};
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: ${COLORS.critical_800};
  }

  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: ${COLORS.neutral_100};
    color: ${COLORS.neutral_300};    
  }
`;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'positive' | 'critical';
  flatEdge?: 'right' | 'left' | 'none' | undefined;
  role?: 'button' | string;
  title?: string;
  size?: Size;
  colorTheme?: 'teal' | 'dark';
  invertFocus?: boolean;
  width?: 'auto' | string;
  loading?: boolean;
  testId?: string;
  icon?: React.ReactNode;
  minWidth?: string;
  iconOnly?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children = '', variant = 'primary', type = 'button', size = Size.Medium, width = 'auto', minWidth = '64px', testId, disabled, flatEdge, icon, ...props }, ref) => {
    // Let's filter out properties that we don't need to render.
    const { loading, ...renderProps } = props;

    const renderContent = () => (
      <>
        {icon && <span className={(children ? 'extramargin ' : '') + (loading ? 'icon loading' : 'icon')}>{icon}</span>}
        {children && <span className={loading ? 'label loading' : 'label'}>{children}</span>}
        {loading && <LoadingIndicator />}
      </>
    );

    let ButtonStyled = ButtonPrimaryStyled;
    switch (variant) {
      case 'secondary':
        ButtonStyled = ButtonSecondaryStyled;
        break;
      case 'tertiary':
        ButtonStyled = ButtonTertiaryStyled;
        break;
      case 'positive':
        ButtonStyled = ButtonPositiveStyled;
        break;
      case 'critical':
        ButtonStyled = ButtonCriticalStyled;
        break;
    }

    return (
      <ButtonStyled
        {...renderProps}
        ref={ref}
        disabled={disabled}
        iconOnly={!children}
        type={type}
        size={size}
        flatEdge={flatEdge}
        width={width}
        minWidth={minWidth}
        data-testid={testId}
        className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}
        onMouseDown={defaultOnMouseDownHandler}>
        <div className="button-content">{renderContent()}</div>
      </ButtonStyled>
    );
  },
);

export default Button;
